import { Title } from "../components/Title"
import { SiteDetail } from "./SiteDetail";
import { MeterDetail } from "./MeterDetail";
import { JobHistory } from "./JobHistory";
import { useState } from "react";
import { useDefinedParams } from "../hooks/useDefinedParams";
import { SiteDetailsLoading } from "./loadingElements/SiteDetailsLoading";
import { MeterDetailsLoading } from "./loadingElements/MeterDetailLoading";
import { getSiteErrorType, getSiteSuccessType, UserPermissions } from "../../../APIDocs/apiTypes";
import { useStoreSite } from "../hooks/useStoreSite";
import { RestTypes, useJbpQuery } from "../hooks/useJbpQuery";
import { Logger } from "../Logger";
import { JobHistoryLoading } from "./loadingElements/JobHistoryLoading";
import { useCanAccess, useIsInternalUser } from "../hooks/useUserPermissions";
import { Model } from "../Model";
import { Typography } from "../components/Typography"
import { Button } from "../components/Button"
import { useNavigate } from "react-router-dom";
import { useNavToRoot } from "../hooks/useNav";


export const Site = () => {

    const [mpan, serviceLine] = useDefinedParams(["mpan", "serviceLine"]);
    const [dataErrorOccurred, setDataErrorOccurred] = useState(false);
    const [fetchCounter, setFetchCounter] = useState(0);
    const storeSiteData = useStoreSite();

    const onSiteData = (siteData: getSiteSuccessType) => {
        storeSiteData.storeSite(mpan, serviceLine, siteData, setDataErrorOccurred);
    }

    const onSiteDataError = (err: getSiteErrorType) => {
        Logger.Log(`Data found to not exist -> ${err?.msg}`)
        setDataErrorOccurred(true);
    }

    const getSiteData = () => {
        if(fetchCounter < 1) {
            setFetchCounter(fetchCounter + 1);
            fetchSiteData();
        }
    }

    const serviceLineHasContractId = (serviceLine: string): boolean => {
        return ['NHH', 'HH', 'MSS', 'MSA'].includes(serviceLine);
    }

    const contractDetails = (item: string) => {
        if(serviceLineHasContractId(serviceLine)) {
            const returnString = Model.GetModel().Get(`model.${item}`);
            if(returnString !== undefined) {
                return `${returnString}`;
            }
        }
        return "";
    }

    const contractDetailsDisplay = contractDetails('contractDisplayName');

    const { isLoading: siteDataLoading, error: siteError, data: siteData, refetch: fetchSiteData } = useJbpQuery<getSiteSuccessType, getSiteErrorType>(
        RestTypes.Get,
        undefined,
        ['site'],
        `jbp/${mpan}/site`,
        false,
        serviceLine,
        onSiteData,
        onSiteDataError
    )

    try {
        const permissionsMap: { [key: string]: keyof UserPermissions } = {
            BNO: 'canUseBNOServiceLine',
            DNO: 'canUseDNOServiceLine',
            HH: 'canUseHHServiceLine',
            NHH: 'canUseNHHServiceLine',
            SUBMETER: 'canUseSubmeterServiceLine',
            MSS: 'canUseMSSServiceLine',
            MSA: 'canUseMSAServiceLine'
        };

        const permissionKey = permissionsMap[serviceLine];
        const canAccess = useCanAccess(permissionKey);

        if(permissionKey == undefined || canAccess == undefined || canAccess === false) {
            return <div>You do not have permissions to view this Service line: {serviceLine}</div>;
        }
    } catch(error) {
        if(error instanceof SyntaxError) {
            //ignore failed parsing, will sort itself out on re-render
            //console.warn('Failed to parse user perms, this is expected until the model is populated at which point a re-render will happen and be successful');
        } else {
            console.error(error);
        }
    }

    getSiteData();

    const nav = useNavigate();
    const routeToSearchScreen = () => {
        useNavToRoot(nav);
    }

    const siteDetailsJBP = 'w-1/6 inline-block align-top';
    const meterDetailsJBP = 'inline-block w-1/6';
    const jobHistoryJBP = 'inline-block w-4/6 align-top';

    return (
        <div id="jbp-site-information-screen">
            <div className="ml-10">
                {serviceLineHasContractId(serviceLine) && useIsInternalUser() ?
                    <div>
                        <Title className="mt-7 mb-0" text={`MPAN: ${mpan}`} />
                        {contractDetailsDisplay && (
                            <Title className="mt-0 mb-0" text={`Contract: ${contractDetailsDisplay}`} />
                        )}
                    </div>
                    :
                    <Title className="mt-7 mb-5" text={`MPAN: ${mpan}`} />
                }
                {siteDataLoading ?
                    <div>
                        <SiteDetailsLoading className={siteDetailsJBP}></SiteDetailsLoading>
                        <MeterDetailsLoading className={meterDetailsJBP}></MeterDetailsLoading>
                        <JobHistoryLoading className={jobHistoryJBP} showTitle={true}></JobHistoryLoading>
                    </div>
                    : dataErrorOccurred && siteError != undefined && (siteError.msg == "No Matching Appointment found for this MPID" || siteError.msg == "No open Appointment for the provided mpxn" || siteError.msg == "No valid MOP appointment for this MPAN" || siteError.msg == "No contract found for the provided mpxn") ?
                        <div>
                            <br></br>
                            <Typography content="No data has been found for the provided MPAN, or you are not authorised to view this site." />
                            <Typography content="Please check the MPAN and Service Line are correct and try again." />
                            <Typography content="If the issue persists please contact support for assistance." />
                            <br></br>
                            <Button text="Back to Search Screen" primary={true} onClick={routeToSearchScreen} className="mt-2" widthStyling="w-1/8" disabled={false} />
                        </div>
                        :
                        <div>
                            <SiteDetail className={siteDetailsJBP} dataErrorOccurred={dataErrorOccurred} data={siteData} error={siteError} />
                            <MeterDetail className={meterDetailsJBP} meters={siteData?.Meters} dataErrorOccurred={dataErrorOccurred} />
                            <JobHistory mpan={mpan} serviceLine={serviceLine} className={jobHistoryJBP} siteData={siteData} dataErrorOccurred={dataErrorOccurred} />
                        </div>
                }
            </div>
        </div>
    )
}
